import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { MessageSquareText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Comments } from '../Comments/Comments';
import { TsrctCommentParentVariant } from '@/generated/gql/graphql';
import { useComments } from '@/legacy/hooks/useComments/useComments';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { useCurrentUser } from '~/hooks/useCurrentUser/useCurrentUser';

interface Comment {
  id: string;
  authorId: string;
  text: string;
  timestamp: string;
  canEdit: boolean;
  canDelete: boolean;
}

interface CommentsPopoverButtonProps {
  entityId: string;
  entityVariant: TsrctCommentParentVariant;
}
export const CommentsPopoverButton = ({ entityId, entityVariant }: CommentsPopoverButtonProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Comments' });
  const [isOpen, setIsOpen] = useState(false);
  const { userId: currentUserId } = useCurrentUser();
  const { hasPermission } = usePermissions();
  const canComment = hasPermission(Permission.CaseCommentCreate);
  const canEditOwnComment = hasPermission(Permission.CaseCommentEdit);
  const canDeleteOwnComment = hasPermission(Permission.CaseCommentDelete);
  const {
    addComment,
    editComment,
    deleteComment,
    commentsData,
    commentsLoading: loading,
    addingComment,
  } = useComments({ entityId, entityVariant, isOpen });

  const comments: Comment[] =
    commentsData?.map((comment) => {
      const isAuthor = currentUserId === comment.authorId;
      return {
        id: comment.id,
        authorId: comment.authorId,
        text: comment.message,
        timestamp: comment.createdAt,
        canEdit: isAuthor && canEditOwnComment,
        canDelete: isAuthor && canDeleteOwnComment,
      };
    }) ?? [];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        render={
          <Button variant="ghost" size="icon" aria-label={t('toggleComments')} className="relative rounded-full">
            <MessageSquareText />
          </Button>
        }
      />
      <PopoverContent align="end" side="bottom" className="w-[400px] max-w-[500px] p-0">
        <Comments
          comments={comments}
          disabled={!canComment}
          loading={loading}
          sending={addingComment}
          onAddComment={addComment}
          onEditComment={editComment}
          onDeleteComment={deleteComment}
        />
      </PopoverContent>
    </Popover>
  );
};
